import { useMemo, useState } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Collapse from '@mui/material/Collapse';
import IconButton from '@mui/material/IconButton';
import Link from '@mui/material/Link';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TablePagination from '@mui/material/TablePagination';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { AccountGroupSchema, AccountSchema, type AccountGroupDto } from '@taqon/contracts';
import { z } from 'zod';
import { Link as RouterLink } from 'react-router';
import { getData, getPage } from '../../shared/api/client';
import { hasCapability, useSession } from '../../shared/session/session';
import { StatusChip } from '../../shared/ui/forms';
import { EmptyState, ErrorState, LoadingState, PageHeader, Section } from '../../shared/ui/page';
import { useUrlList } from '../../shared/ui/url-list';
import { SearchFilterBar } from '../../shared/ui/filters';

export const ACCOUNTS = '/api/v1/ledger/accounts';
export const ACCOUNT_GROUPS = '/api/v1/ledger/account-groups';

/** The L1 to L4 groups, read once: the approved COA has about two hundred. */
export function useAccountGroups(enabled = true) {
  return useQuery({
    queryKey: ['ledger', 'account-groups'],
    enabled,
    staleTime: 60_000,
    queryFn: () => getData(ACCOUNT_GROUPS, z.array(AccountGroupSchema)),
  });
}

/** An account's place in the hierarchy, as `L2 name › L3 name › L4 name` (FRD-FIN-011). */
export function pathText(path: readonly { code: string; name: string }[]): string {
  return path.map((segment) => `${segment.code} ${segment.name}`).join(' › ');
}

/**
 * The Chart of Accounts (FRD-FIN-001, FIN-002, FIN-011, FIN-018): every account with its
 * hierarchy path and classification, filtered on the server by search, status, statement, account
 * type, module tag and hierarchy group, with the filters held in the URL. The hierarchy browser
 * narrows the list to a group at any level (FRD-FIN-004). Each account opens its own page.
 */
export function ChartOfAccountsPage() {
  const session = useSession();
  const canImport = hasCapability(session.data, 'ledger.account:create');
  const canSeeGroups = hasCapability(session.data, 'ledger.account_group:view');
  const list = useUrlList({ pageSize: 50 });
  const params = list.apiParams(
    {
      search: 'search',
      status: 'status',
      statement: 'statement',
      type: 'account_type',
      module: 'module',
      group: 'group',
      posting: 'posting',
    },
    list.get('sort') === 'name' ? 'name' : 'code',
  );
  const accounts = useQuery({
    queryKey: ['accounts', params.toString()],
    placeholderData: keepPreviousData,
    queryFn: () => getPage(`${ACCOUNTS}?${params.toString()}`, AccountSchema),
  });
  const groups = useAccountGroups(canSeeGroups);
  const selectedGroup = groups.data?.find((g) => g.id === list.get('group'));

  const select = (
    label: string,
    key: string,
    options: readonly [string, string][],
    minWidth = 170,
  ) => (
    <TextField
      select
      size="small"
      label={label}
      value={list.get(key)}
      sx={{ minWidth }}
      onChange={(e) => {
        list.set(key, e.target.value);
      }}
    >
      <MenuItem value="">Any</MenuItem>
      {options.map(([value, text]) => (
        <MenuItem key={value} value={value}>
          {text}
        </MenuItem>
      ))}
    </TextField>
  );

  return (
    <Stack spacing={3}>
      <PageHeader
        title="Chart of accounts"
        description="The approved TAQON accounts with their hierarchy, classification and posting controls."
        actions={
          canImport ? (
            <Button component={RouterLink} to="/coa-import" variant="outlined">
              COA import
            </Button>
          ) : undefined
        }
      />
      <Box role="search" aria-label="Filter the accounts">
        <SearchFilterBar
          label="Search code or name"
          value={list.get('search')}
          onChange={(value) => {
            list.set('search', value);
          }}
        >
          {select('Status', 'status', [
            ['active', 'Active'],
            ['inactive', 'Inactive'],
          ])}
          {select('Statement', 'statement', [
            ['Balance Sheet', 'Balance Sheet'],
            ['P&L', 'Profit & Loss'],
          ])}
          {select('Posting', 'posting', [
            ['true', 'Posting accounts'],
            ['false', 'Non-posting'],
          ])}
          <TextField
            size="small"
            label="Account type"
            value={list.get('type')}
            helperText="e.g. Expense"
            sx={{ minWidth: 160 }}
            onChange={(e) => {
              list.set('type', e.target.value);
            }}
          />
          <TextField
            size="small"
            label="Module tag"
            value={list.get('module')}
            helperText="e.g. Procurement"
            sx={{ minWidth: 160 }}
            onChange={(e) => {
              list.set('module', e.target.value);
            }}
          />
          {select('Sort by', 'sort', [['name', 'Account name']], 150)}
        </SearchFilterBar>
      </Box>
      <Box
        sx={{
          display: 'grid',
          gap: 3,
          gridTemplateColumns: { xs: '1fr', lg: canSeeGroups ? '320px 1fr' : '1fr' },
          alignItems: 'start',
        }}
      >
        {canSeeGroups && (
          <HierarchyBrowser
            groups={groups.data ?? []}
            loading={groups.isPending}
            selected={list.get('group')}
            onSelect={(id) => {
              list.set('group', id);
            }}
          />
        )}
        <Section
          title={
            selectedGroup === undefined
              ? 'Accounts'
              : `Accounts under ${selectedGroup.level} ${selectedGroup.code} ${selectedGroup.name}`
          }
          actions={
            selectedGroup !== undefined ? (
              <Button
                size="small"
                onClick={() => {
                  list.set('group', '');
                }}
              >
                Show all
              </Button>
            ) : undefined
          }
        >
          {accounts.isError && <ErrorState message={accounts.error.message} />}
          {accounts.isPending && <LoadingState label="Loading accounts" />}
          {accounts.data !== undefined && accounts.data.items.length === 0 && (
            <EmptyState
              title="No accounts found"
              detail="The Chart of Accounts is loaded through COA import."
            />
          )}
          {accounts.data !== undefined && accounts.data.items.length > 0 && (
            <>
              <Table size="small" aria-label="Accounts">
                <TableHead>
                  <TableRow>
                    <TableCell>Code</TableCell>
                    <TableCell>Name</TableCell>
                    <TableCell>Hierarchy</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell>Statement</TableCell>
                    <TableCell>Module</TableCell>
                    <TableCell>Posting</TableCell>
                    <TableCell>Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {accounts.data.items.map((account) => (
                    <TableRow key={account.id} hover>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        <Link
                          component={RouterLink}
                          to={`/accounts/${account.id}`}
                          sx={{ fontWeight: 600 }}
                        >
                          {account.code}
                        </Link>
                      </TableCell>
                      <TableCell>{account.name}</TableCell>
                      <TableCell sx={{ color: 'text.secondary', fontSize: 13 }}>
                        {pathText(account.path)}
                      </TableCell>
                      <TableCell>{account.accountType}</TableCell>
                      <TableCell>{account.financialStatement}</TableCell>
                      <TableCell>{account.moduleTag}</TableCell>
                      <TableCell>{account.isPosting ? 'Yes' : 'No'}</TableCell>
                      <TableCell>
                        <StatusChip status={account.status} />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <TablePagination
                component="div"
                count={accounts.data.total}
                page={list.page}
                rowsPerPage={list.pageSize}
                rowsPerPageOptions={[25, 50, 100]}
                onPageChange={(_, next) => {
                  list.setPage(next);
                }}
                onRowsPerPageChange={(e) => {
                  list.setPageSize(Number.parseInt(e.target.value, 10));
                }}
              />
            </>
          )}
        </Section>
      </Box>
    </Stack>
  );
}

/**
 * The L1 to L4 hierarchy as a tree (FRD-FIN-001, FIN-003): each group expands to its children and
 * choosing one narrows the account list to everything beneath it.
 */
function HierarchyBrowser({
  groups,
  loading,
  selected,
  onSelect,
}: {
  groups: readonly AccountGroupDto[];
  loading: boolean;
  selected: string;
  onSelect: (id: string) => void;
}) {
  const children = useMemo(() => {
    const map = new Map<string | null, AccountGroupDto[]>();
    for (const group of groups) {
      const list = map.get(group.parentGroupId) ?? [];
      list.push(group);
      map.set(group.parentGroupId, list);
    }
    for (const list of map.values()) list.sort((a, b) => a.code.localeCompare(b.code));
    return map;
  }, [groups]);
  const [open, setOpen] = useState<ReadonlySet<string>>(new Set());
  const toggle = (id: string) => {
    setOpen((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };
  const render = (parent: string | null, depth: number) =>
    (children.get(parent) ?? []).map((group) => {
      const kids = children.get(group.id) ?? [];
      const expanded = open.has(group.id);
      return (
        <Box key={group.id}>
          <Stack direction="row" sx={{ alignItems: 'center', pl: depth * 1.5 }}>
            {kids.length > 0 ? (
              <IconButton
                size="small"
                aria-label={`${expanded ? 'Collapse' : 'Expand'} ${group.code} ${group.name}`}
                onClick={() => {
                  toggle(group.id);
                }}
              >
                {expanded ? (
                  <ExpandLessIcon fontSize="small" />
                ) : (
                  <ExpandMoreIcon fontSize="small" />
                )}
              </IconButton>
            ) : (
              <Box sx={{ width: 34 }} />
            )}
            <ListItemButton
              dense
              selected={selected === group.id}
              onClick={() => {
                onSelect(group.id);
              }}
              sx={{ borderRadius: 1, py: 0.25 }}
            >
              <ListItemText
                primary={`${group.code} ${group.name}`}
                secondary={group.level}
                slotProps={{ primary: { sx: { fontSize: 14 } } }}
              />
            </ListItemButton>
          </Stack>
          {kids.length > 0 && (
            <Collapse in={expanded} unmountOnExit>
              {render(group.id, depth + 1)}
            </Collapse>
          )}
        </Box>
      );
    });
  return (
    <Section title="Hierarchy">
      {loading && <LoadingState label="Loading the hierarchy" />}
      {!loading && groups.length === 0 && <EmptyState title="No account groups yet" />}
      {groups.length > 0 && (
        <List dense aria-label="Account hierarchy" sx={{ maxHeight: 640, overflowY: 'auto', p: 1 }}>
          {render(null, 0)}
        </List>
      )}
    </Section>
  );
}
